/**
 * Krok CZ1 — KONTROLA JAZYKA TEXTU URCENEHO CLOVEKU.
 *
 * Nalez R.B. 27. 9. 2026 (screenshoty adminu, audit LV 72 z 1. 9. 2026):
 * otevrene otazky a poznamky faktu chodily ve tvaru „Pokud ano, mame primy
 * asking price a technicky popis domu", „Maklepka se vyslovne ptala" a
 * „note: Soucet KN vymer … (VERIFIED ČÚZK/RÚIAN, 2026-09-01) … · deltaM2: 303".
 * Pricina byla dvoji: prompt auditniho agenta byl cely bez diakritiky a nerekl,
 * v jakem jazyce se pise (model styl zrcadli), a UI tisklo surova interni pole.
 *
 * Tenhle modul je DETERMINISTICKA cast opravy — zadny model, zadne IO, ctyri
 * tridy vady a u kazde konkretni misto:
 *
 *   INTERNAL_TOKEN     — interni kod nebo nazev pole („note:", „deltaM2",
 *                        „reason_code", „VERIFIED"),
 *   ENGLISH_WORD       — anglicismus misto ceskeho slova („asking price"),
 *   TYPO               — znamy preklep („Maklepka" → „makléřka"),
 *   MISSING_DIACRITICS — cesky text psany bez hacku a carek („mame" → „máme").
 *
 * PROC EXPLICITNI SEZNAMY, a ne „kazde slovo bez hacku": spousta ceskych slov
 * diakritiku nema (adresa, pozemek, katastr, doklad, pokud) a spousta zkratek
 * je velkymi pismeny pravem (ČÚZK, RÚIAN, CHKO, PENB, SVJ). Detektor, ktery
 * pali na cokoli, se po treti falesne blokaci vypne — proto seznamy rostou
 * z toho, co se v provozu SKUTECNE objevilo, stejne jako `diacritics.ts`.
 *
 * ZDROJ JAZYKOVEHO PRAVIDLA PRO PROMPTY je `HUMAN_TEXT_RULE_LINES` nize —
 * veta, kterou cte model, a kontrola, ktera ji vynucuje, ziji v jednom souboru,
 * aby se nemohly rozejit.
 */
import { MISSING_DIACRITICS_RULES } from './diacritics.js';
/** Tridy vady. Strojove kody — text az v i18n katalogu (`humanText.issue.*`). */
export const HUMAN_TEXT_ISSUE_CODES = [
    'INTERNAL_TOKEN',
    'ENGLISH_WORD',
    'TYPO',
    'MISSING_DIACRITICS',
];
/**
 * Slova, ktera v ceskem textu o nemovitosti diakritiku MAJI a v provozu
 * chodila bez ni. Doplnuje `MISSING_DIACRITICS_RULES` (krok C2b — tam jde
 * o hodnoty katalogu); tenhle seznam je slovnik auditniho behu.
 *
 * NENI tu „pokud", „nemovitost", „adresa", „pozemek", „katastr" ani „domu":
 * to jsou spravne ceske tvary BEZ diakritiky a pravidlo na ne by bylo falesny
 * poplach, ne kontrola (zadani CZ1 je uvadi jako heuristiku — viz
 * REPORT-step-CZ1 ROZHODNUTI).
 */
export const AUDIT_DIACRITICS_RULES = [
    { pattern: /\bmame\b/i, correct: 'máme' },
    { pattern: /\bvyslovne\b/i, correct: 'výslovně' },
    { pattern: /\bprimy\b/i, correct: 'přímý' },
    { pattern: /\bsoucet\b/i, correct: 'součet' },
    { pattern: /\bvymera\b/i, correct: 'výměra' },
    { pattern: /\bvymery\b/i, correct: 'výměry' },
    { pattern: /\bvymer\b/i, correct: 'výměr' },
    { pattern: /\brozdil\b/i, correct: 'rozdíl' },
    { pattern: /\bpredmet\b/i, correct: 'předmět' },
    { pattern: /\bjednoznacne\b/i, correct: 'jednoznačně' },
    { pattern: /\burcen\b/i, correct: 'určen' },
    { pattern: /\bprodavajici\b/i, correct: 'prodávající' },
    { pattern: /\bkupujici\b/i, correct: 'kupující' },
    { pattern: /\bnajemce\b/i, correct: 'nájemce' },
    { pattern: /\bmaklerka\b/i, correct: 'makléřka' },
    { pattern: /\bmakler\b/i, correct: 'makléř' },
    { pattern: /\bvlastnik\b/i, correct: 'vlastník' },
    { pattern: /\bovereno\b/i, correct: 'ověřeno' },
    { pattern: /\boverit\b/i, correct: 'ověřit' },
    { pattern: /\bzapis\b/i, correct: 'zápis' },
    { pattern: /\bpristup\b/i, correct: 'přístup' },
    { pattern: /\bnapojeni\b/i, correct: 'napojení' },
    { pattern: /\bpronajem\b/i, correct: 'pronájem' },
    { pattern: /\bneni\b/i, correct: 'není' },
    { pattern: /\bmuze\b/i, correct: 'může' },
    { pattern: /\bzadny\b/i, correct: 'žádný' },
    { pattern: /\bzadna\b/i, correct: 'žádná' },
    { pattern: /\bvsechny\b/i, correct: 'všechny' },
    { pattern: /\bdulezite\b/i, correct: 'důležité' },
    { pattern: /\bpuvodni\b/i, correct: 'původní' },
    /*
     * Krok FUNK1 bod 6 — slova z nalezu R.B. 4. 10. 2026 (zasadni problemy na
     * detailu zakazky: „Rozdil 30 m2 … muze byt jen prepis", „Rozhoduje, zda
     * lze rovnou zadat uzemni/stavebni rizeni"). „rozdil" a „muze" uz seznam
     * mel, zbytek ne.
     */
    { pattern: /\bprepis\b/i, correct: 'přepis' },
    { pattern: /\brizeni\b/i, correct: 'řízení' },
    { pattern: /\buzemni\b/i, correct: 'územní' },
    { pattern: /\bstavebni\b/i, correct: 'stavební' },
    { pattern: /\bprodava\b/i, correct: 'prodává' },
    { pattern: /\bpresne\b/i, correct: 'přesné' },
    { pattern: /\bocenovani\b/i, correct: 'oceňování' },
];
const DIACRITICS_RULES = [
    ...MISSING_DIACRITICS_RULES,
    ...AUDIT_DIACRITICS_RULES,
];
/**
 * Anglicismy, ktere v ceskem textu pro klienta nemaji co delat. Seznam je uzky
 * zamerne: „report" v cestine tohohle produktu zdomacnel (technicky report),
 * takze tady NENI. Naopak „asking price", „owner" nebo „deadline" jsou presne
 * ta slova, ktera model vzal z anglickeho kontextu.
 */
export const ENGLISH_BLOCKLIST = [
    { pattern: /\basking\s+price\b/i, correct: 'nabídková cena' },
    { pattern: /\basking\b/i, correct: 'nabídková cena' },
    { pattern: /\bprice\b/i, correct: 'cena' },
    { pattern: /\bowner\b/i, correct: 'vlastník' },
    { pattern: /\bseller\b/i, correct: 'prodávající' },
    { pattern: /\bbuyer\b/i, correct: 'kupující' },
    { pattern: /\bdeadline\b/i, correct: 'termín' },
    { pattern: /\bdownside\b/i, correct: 'riziko poklesu hodnoty' },
    { pattern: /\blandlocked\b/i, correct: 'bez přístupu z veřejné komunikace' },
    { pattern: /\bchecklist\b/i, correct: 'seznam kroků' },
    { pattern: /\bmismatch\b/i, correct: 'nesoulad' },
    { pattern: /\bissue\b/i, correct: 'problém' },
    { pattern: /\bupdate\b/i, correct: 'aktualizace' },
    { pattern: /\btimeline\b/i, correct: 'časová osa' },
    { pattern: /\bavailable\b/i, correct: 'dostupné' },
    { pattern: /\bunknown\b/i, correct: 'nezjištěno' },
];
/**
 * Zjevne preklepy. Kratky, jmenovity seznam — roste z toho, co se objevilo
 * („Maklepka se vyslovne ptala", screenshot 27. 9. 2026).
 */
export const TYPO_RULES = [
    { pattern: /\bmakl[eé]pk[ay]\b/i, correct: 'makléřka' },
    { pattern: /\bnemovistost\w*\b/i, correct: 'nemovitost' },
    { pattern: /\bkatastralni\b/i, correct: 'katastrální' },
];
/**
 * Interni stavove kody. Vypisuji se jmenovite — obecne pravidlo „slovo velkymi
 * pismeny" by palilo na ČÚZK, RÚIAN, CHKO, PENB nebo ISIR, ktere v ceskem
 * textu pravem jsou.
 */
const INTERNAL_CODE_WORDS = [
    'VERIFIED',
    'SUPPORTED',
    'INFERRED',
    'CONFLICT',
    'UNVERIFIED',
    'NEEDS_REVIEW',
    'NOT_DETERMINED',
    'PROBABLY_YES',
    'PROBABLY_NO',
    'USER_INPUT',
    'OWNER_CLAIM',
    'BLOCKED',
    'CRITICAL',
    'MEDIUM',
    'BROKER',
    'SYSTEM',
];
/**
 * Jednotky, kde po malem pismenu legitimne nasleduje velke (kWh, hPa, mSv).
 * Bez nich by pravidlo na camelCase palilo na spotrebu energie v PENB.
 */
const UNIT_ALLOWLIST = [
    'kWh',
    'kWp',
    'kW',
    'kVA',
    'MWh',
    'hPa',
    'dB',
    'mSv',
    'pH',
];
const INTERNAL_TOKEN_RULES = [
    /** Popiska pole vypsana do textu: „note: …". */
    { pattern: /\bnote\s*:/giu, expected: 'poznámka jako věta, bez názvu pole' },
    /** `reason_code`, `dd_facts`, `SOURCE_MISSING` — cokoli s podtrzitkem. */
    { pattern: /\b[\p{L}\d]+_[\p{L}\d_]+\b/gu, expected: null },
    /** camelCase nazev pole: `deltaM2`, `questionKey`, `knVerifiedSumM2`. */
    { pattern: /\b\p{Ll}[\p{Ll}\d]*\p{Lu}[\p{L}\d]*\b/gu, expected: null },
    /** Samotna „delta" je taky nazev pole, ne ceske slovo. */
    { pattern: /\bdelta\b/giu, expected: 'rozdíl' },
    { pattern: new RegExp(`\\b(?:${INTERNAL_CODE_WORDS.join('|')})\\b`, 'gu'), expected: null },
];
/** Co se pred kontrolou z textu vyhodi: odkazy nesou podtrzitka i camelCase. */
const URL_RE = /(?:https?:\/\/|www\.)\S+/giu;
/**
 * Nazev ICU argumentu (`{owner}`, `{nearestM, number}`) je IDENTIFIKATOR, ne
 * slovo pro cloveka — do kontroly nepatri. Text ve vetvich `select`/`plural`
 * (`water{vodovod}`, `other{máme to}`) se naopak kontroluje dal.
 *
 * Rozlisujeme je podle toho, co `{` predchazi: telo vetve nasleduje hned za
 * jmenem vetve (`other{`, `=0{`), odkaz na argument stoji po mezere, na zacatku
 * nebo po jine `{`.
 */
const ICU_ARG_RE = /(?<![\p{L}\p{N}_=])\{\s*([A-Za-z_][A-Za-z0-9_]*)/gu;
/** Maskovani pred kontrolou: odkazy cele, z ICU jen jmeno argumentu. */
function maskNonProse(text) {
    const spaces = (length) => ' '.repeat(length);
    return text
        .replace(URL_RE, (match) => spaces(match.length))
        .replace(ICU_ARG_RE, (match, name) => match.slice(0, match.length - name.length) + spaces(name.length));
}
function maskRange(chars, from, to) {
    for (let index = from; index < to && index < chars.length; index += 1)
        chars[index] = ' ';
}
function globalRegex(pattern) {
    const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
    return new RegExp(pattern.source, flags);
}
/**
 * Vsechny nalezy v jednom textu. Poradi je pevne (INTERNAL_TOKEN → ENGLISH_WORD
 * → TYPO → MISSING_DIACRITICS) a kazde nalezene misto se zamaskuje, takze jedno
 * slovo nikdy nespadne do dvou trid („VERIFIED" je interni kod, ne anglicke
 * slovo). Kontrola je CISTA: zadny model, zadne IO, zadny cas.
 */
export function checkHumanText(text) {
    const chars = [...maskNonProse(text)];
    const issues = [];
    const seen = new Set();
    const push = (code, found, expected) => {
        const key = `${code}:${found.toLowerCase()}`;
        if (seen.has(key))
            return;
        seen.add(key);
        issues.push({ code, found, expected });
    };
    const scan = (code, rules, allow = () => false) => {
        for (const rule of rules) {
            const re = globalRegex(rule.pattern);
            const haystack = chars.join('');
            let match;
            while ((match = re.exec(haystack)) !== null) {
                if (match[0].length === 0) {
                    re.lastIndex += 1;
                    continue;
                }
                if (!allow(match[0])) {
                    push(code, match[0], rule.correct ?? rule.expected ?? null);
                    maskRange(chars, match.index, match.index + match[0].length);
                }
            }
        }
    };
    scan('INTERNAL_TOKEN', INTERNAL_TOKEN_RULES, (found) => UNIT_ALLOWLIST.includes(found));
    scan('ENGLISH_WORD', ENGLISH_BLOCKLIST);
    scan('TYPO', TYPO_RULES);
    scan('MISSING_DIACRITICS', DIACRITICS_RULES);
    return issues;
}
/** Projde text kontrolou bez jedine vytky? */
export function isHumanTextClean(text) {
    return checkHumanText(text).length === 0;
}
/**
 * Strojovy popis nalezu do `detail` duvodu brany a do `attrs` otazky — kody
 * a nalezena slova, NIKDY cely text (ten do metrik ani do casove osy nepatri,
 * stejne jako v A2).
 */
export function describeHumanTextIssues(issues) {
    return issues.map((issue) => `${issue.code}:${issue.found}`).join(', ');
}
/** Kody nalezu bez duplicit — rychly prehled pro metriky a UI. */
export function humanTextIssueCodes(issues) {
    return [...new Set(issues.map((issue) => issue.code))];
}
/**
 * JAZYKOVE PRAVIDLO PRO PROMPTY — tataz veta, kterou vynucuje `checkHumanText`.
 * Vklada se do briefu auditniho agenta (`apps/worker/src/audit-queue/brief.ts`)
 * i do systemovych promptu roli, ktere pisi text pro cloveka (Redaktor,
 * Korespondent, shrnuti pro revizora).
 */
export const HUMAN_TEXT_RULE_LINES = [
    'Všechny texty určené člověku (otevřené otázky, poznámky k faktům, nálezy, důvody,',
    'úkoly pro člověka, e-maily) piš spisovnou češtinou S DIAKRITIKOU a v celých větách.',
    'Bez angličtiny: „asking price“ je „nabídková cena“, „owner“ je „vlastník“,',
    '„deadline“ je „termín“. Bez interních kódů a názvů polí: nikdy „note:“,',
    '„reason_code“, „deltaM2“ ani „VERIFIED“ uvnitř věty.',
    'Kódy patří VÝHRADNĚ do strukturovaných polí (reason_code, status, klíč faktu),',
    'ne do textu, který bude někdo číst.',
];
/** Jazykove pravidlo jako jeden blok promptu. */
export function humanTextRuleBlock(heading = '## Jazyk textů pro člověka') {
    return [heading, ...HUMAN_TEXT_RULE_LINES].join('\n');
}
// ------------------------------------------------- osloveni maklere (vykani)
/**
 * Krok PIL1 (vada V3 generalky GEN1, zadani R.B. 5. 10.): aplikace mluvila na
 * maklere RAZ VYKANIM, RAZ TYKANIM — na jedne obrazovce stalo „Dnes na vás nic
 * nehoří" a hned pod tim „Nic na tebe nečeká". JEDNO osloveni pro celou
 * administraci je **vykani**.
 *
 * Vzory jsou VYCET toho, co se v katalogu skutecne objevilo (stejna konvence
 * jako `TYPO_RULES` a `MISSING_DIACRITICS_RULES`): obecne pravidlo „2. osoba
 * jednotneho cisla" v cestine regularnim vyrazem napsat nejde — koncovka
 * `-áš` je i ve slove „váš".
 *
 * Hranice slova je `(?<!\p{L})…(?!\p{L})`, NE `\b`: `\b` v JavaScriptu stoji
 * na ASCII `\w`, takze „posuňte" by na vzoru `\bposuň\b` spadlo (mezi `ň` a
 * `t` je pro nej hranice) a kontrola by hlasila vykani jako tykani.
 *
 * Krok E2E2: pravidlo zije TADY a ne v testu, protoze ho ctou DVE brany —
 * `packages/platform/i18n/test/i18n.spec.ts` (texty katalogu `webAdmin.*`
 * a `mcp.checks.*`) a `test/preview-sada-fixtury.spec.ts` (co po tech textech
 * CEKA regresni sada proti preview). Dve kopie teze pravdy se vzdy rozejdou
 * a presne to se 5. 10. stalo: katalog uz vykal, sada jeste cekala „tvoje"
 * a „ty" a brana po deployi zastavila linku.
 */
export const INFORMAL_ADDRESS_PATTERNS = [
    { vzor: /(?<!\p{L})tebe(?!\p{L})/iu, popis: 'tebe' },
    { vzor: /(?<!\p{L})tobě(?!\p{L})/iu, popis: 'tobě' },
    { vzor: /(?<!\p{L})tebou(?!\p{L})/iu, popis: 'tebou' },
    { vzor: /(?<!\p{L})tv(ůj|oje|oji|á|é|ého|ému|ým|ou|ojí|ých|ými)(?!\p{L})/iu, popis: 'tvůj / tvoje' },
    {
        vzor: /(?<!\p{L})(máš|nemáš|vidíš|uvidíš|budeš|můžeš|nemůžeš|chceš|víš|nevíš|dostaneš|doplníš|doplňuješ|vkládáš|vyplníš|upravuješ|upravíš|pošleš|odešleš|zveřejníš|zkontroluješ|potřebuješ|najdeš|začneš|vedeš)(?!\p{L})/iu,
        popis: '2. osoba jednotného čísla',
    },
    {
        vzor: /(?<!\p{L})(zkus|otevři|zkontroluj|potvrď|dokonči|vyber|klikni|klepni|napiš|zadej|přidej|doplň|pošli|připoj|zapni|vytvoř|vlož|nahraj|pokračuj|nahlas|posuň|rozhodni|zeptej|ověř|ulož|smaž|zruš|nastav|přejdi|počkej|stáhni|vrať|zvol|přepni|zapiš|vyfoť|omez)(?!\p{L})/iu,
        popis: 'rozkaz v jednotném čísle',
    },
];
/**
 * Krok UXP6: kodove jmeno nasho kroku ze zadani („krok ES1", „(ADM1)") je
 * PROVOZNI info RedHand. Makler z nej necte nic — zna produkt, ne nas
 * backlog — a v aplikaci kancelare proto nema co delat.
 *
 * Vzor je zamerne KONTEXTOVY, ne jen tvarovy: `ERA5`, `PM2,5` nebo `S256`
 * maji presne tentyz tvar (velka pismena + cislice), ale jsou to legitimni
 * vnejsi normy. Hlasi se proto jen token, ktery stoji za slovem „krok" /
 * „step", nebo sam v zavorce — tam uz to je odkaz do naseho zadani. Mereno
 * nad celym katalogem (10 286 klicu, cs i en): 2 nalezy, 0 falesnych.
 *
 * SSOT pro branu: `test/human-text.spec.ts` to vola nad cely katalog, takze
 * novy text s kodovym jmenem kroku neprojde. Vyjimka patri SYMBOLU (klici),
 * ne souboru (CLAUDE.md §8.3).
 */
const INTERNAL_STEP_CODENAME_RE = /(?:(?<![\p{L}])(?:krok|kroku|kroce|kroky|step)\s+([A-Z]{2,5}\d{1,2}[a-z]?)(?![\p{L}\d]))|(?:\(([A-Z]{2,5}\d{1,2}[a-z]?)\))/gu;
/** Kodova jmena nasich kroku v textu pro cloveka (bez duplicit). */
export function findInternalStepCodenames(text) {
    const found = new Set();
    for (const hit of text.matchAll(INTERNAL_STEP_CODENAME_RE)) {
        const codename = hit[1] ?? hit[2];
        if (codename !== undefined)
            found.add(codename);
    }
    return [...found];
}
/** Prvni nalez tykani v textu, nebo `null`. */
export function informalAddress(text) {
    for (const { vzor, popis } of INFORMAL_ADDRESS_PATTERNS) {
        const hit = vzor.exec(text);
        if (hit !== null)
            return { found: hit[0], popis };
    }
    return null;
}
//# sourceMappingURL=human-text.js.map